import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classes from './LightboxModal.module.scss';
import { Modal } from '../../../../components/containers/Modal';
import { Void } from '../../../../models/basic-objects';
import { ButtonColorsEnum } from '../../../../constants/button-colors';
import { MediaItem } from '../../../../models/media';
import { MediaType, ThirdPartyPostMedia, ThirdPartyPostMediaType } from '../../../../models/graphql';
import { MediaGridContext } from '../MediaGrid/MediaGrid.view';
import { PrimaryButtonType, SecondaryButtonType } from '../../../../components/containers/Modal/Modal.view';
import YouTube from 'react-youtube';
import { useDimensions } from '../../../../hooks/useDimensions';
import { useDidMount } from '../../../../hooks/useDidMount';

export const extractYouTubeID = (url: string) => {
  const regex =
    /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/gi;
  const match = regex.exec(url);
  return match ? match[1] : undefined;
};

export const VideoPlayer = ({ url }: { url: string }): JSX.Element => {
  const youTubeId = extractYouTubeID(url);

  return (
    <>
      {youTubeId ? (
        <YouTube
          videoId={youTubeId}
          opts={{
            playerVars: {
              autoplay: 1
            }
          }}
          className={classes.LightboxModal}
        />
      ) : (
        <video controls autoPlay={true} className={classes.LightboxModal}>
          <source src={url} />
          <div>Your browser does not support the video tag.</div>
        </video>
      )}
    </>
  );
};

export type LightboxModalViewProps = {
  onClose: Void;
  mediaItem: MediaItem | ThirdPartyPostMedia;
  isUnsplash?: boolean;
  context: MediaGridContext;
  primaryButtonAction?: VoidFunction;
  secondaryButtonAction?: VoidFunction;
};

export const LightboxModalView = (props: LightboxModalViewProps): JSX.Element => {
  const { mediaItem, context, onClose, primaryButtonAction, isUnsplash, secondaryButtonAction } = props;

  const [mounted, setMounted] = useState<Boolean>(false);
  const [truncatedText, setTruncatedText] = useState<string>('');

  let actionButtons: PrimaryButtonType | SecondaryButtonType | undefined;
  switch (context) {
    case MediaGridContext.POST_EDITOR_MEDIA_LIBRARY:
      actionButtons = primaryButtonAction
        ? {
            primaryButtonAction: primaryButtonAction,
            primaryButtonTitle: 'Add to Post',
            primaryButtonColor: ButtonColorsEnum.primary
          }
        : undefined;
      break;
    case MediaGridContext.POST_EDITOR_FIND_IMAGES:
      actionButtons =
        primaryButtonAction && secondaryButtonAction
          ? {
              primaryButtonTitle: 'Add to Post',
              primaryButtonAction: primaryButtonAction,
              primaryButtonColor: ButtonColorsEnum.primary
            }
          : undefined;
      break;
    case MediaGridContext.FIND_IMAGES:
      actionButtons =
        primaryButtonAction && secondaryButtonAction
          ? {
              primaryButtonTitle: 'Share in a Post',
              primaryButtonAction: primaryButtonAction,
              primaryButtonColor: ButtonColorsEnum.primary,
              secondaryButtonTitle: 'Add to Library',
              secondaryButtonAction: secondaryButtonAction,
              secondaryButtonColor: ButtonColorsEnum.primary
            }
          : undefined;
      break;
    case MediaGridContext.PAGE:
      actionButtons = primaryButtonAction
        ? {
            primaryButtonTitle: 'Share in a Post',
            primaryButtonAction: primaryButtonAction,
            primaryButtonColor: ButtonColorsEnum.primary
          }
        : undefined;
      break;
    default:
      actionButtons = undefined;
  }

  const imageRef = useRef<HTMLImageElement | null>(null);
  const { width: imageWidth } = useDimensions(imageRef);

  const measureTextWidth = (text: string, font: string): number => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context) return text.length * 10; // Fallback estimate if canvas is unavailable

    context.font = font;

    return context.measureText(text).width;
  };

  const truncateStringWithEllipsis = useCallback(
    (text: string): string => {
      const font = 'Proxima Nova W07, arial, sans-serif';
      const maxWidth = imageWidth < 252 ? 252 : imageWidth;
      let currentWidth = measureTextWidth(text, font);

      if (currentWidth <= maxWidth) {
        return text;
      }

      let truncatedText = text;
      while (measureTextWidth(truncatedText + '…', font) > maxWidth && truncatedText.length > 0) {
        truncatedText = truncatedText.slice(0, -1);
      }

      return truncatedText + '…';
    },
    [imageWidth]
  );

  const unsplashCredits = useMemo(() => {
    const style = {
      maxWidth: imageWidth > 252 ? imageWidth : 252 // take into consideration padding/marign
    };

    return 'createdBy' in mediaItem && mediaItem.createdBy && isUnsplash ? (
      <div className={classes.UnsplashCreditsContainer} style={style}>
        <div>
          Photo by
          <a
            href={`https://unsplash.com/@${mediaItem.createdByUsername}?utm_source=smarterqueue&utm_medium=referral`}
            target={'_blank'}
            rel={'noreferrer'}
            title={mediaItem.createdBy}
            className={classes.UnsplashCreditLink}>
            {truncatedText}
          </a>
          {` on `}
          <a
            href={'https://unsplash.com/?utm_source=smarterqueue&utm_medium=referral'}
            target={'_blank'}
            rel={'noreferrer'}>
            Unsplash
          </a>
        </div>
      </div>
    ) : undefined;
  }, [imageWidth, isUnsplash, mediaItem, truncatedText]);

  useEffect(() => {
    if (mounted && 'createdBy' in mediaItem && mediaItem.createdBy && isUnsplash) {
      setTruncatedText(truncateStringWithEllipsis(` ${mediaItem.createdBy}`));
    }
  }, [mediaItem, truncateStringWithEllipsis, isUnsplash, mounted, imageWidth]); // Run when ref or content changes

  useDidMount(() => {
    setMounted(true);
  });

  return (
    <Modal
      title={unsplashCredits || ' '}
      onClose={onClose}
      showCloseIcon={true}
      size={'hugging'}
      compensateForScroll={true}
      {...actionButtons}>
      <div className={classes.MediaWrapper}>
        {(() => {
          switch (mediaItem.type) {
            case MediaType.VIDEO:
            case ThirdPartyPostMediaType.VIDEO:
              return <VideoPlayer url={String(mediaItem.url)} />;
            case ThirdPartyPostMediaType.PHOTO:
              return <img src={mediaItem.url ?? ''} className={classes.LightboxModal} alt={''} ref={imageRef} />;
            case MediaType.PHOTO:
              return <img src={mediaItem.thumbnailUrl} className={classes.LightboxModal} alt={mediaItem.fileName} />;
            default:
              return null;
          }
        })()}
      </div>
    </Modal>
  );
};
